<template>
  <div class="min-h-screen bg-white">
    <!-- Hero Section -->
    <section class="bg-gradient-to-br from-purple-50 to-violet-100 py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <h1 class="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Cas d'usage
          </h1>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez comment Anamnesis transforme concrètement la documentation patrimoniale à travers des exemples réels
          </p>
        </div>
      </div>
    </section>

    <!-- Cas d'usage principaux -->
    <section class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
          <!-- Cas d'usage 1: Fouilles archéologiques -->
          <div class="bg-gradient-to-br from-amber-50 to-orange-100 rounded-2xl p-8 shadow-lg">
            <div class="mb-6">
              <img 
                src="https://placeholderimage.eu/api/600/300?category=monument" 
                alt="Fouilles archéologiques" 
                class="w-full h-48 object-cover rounded-lg shadow-md"
              />
            </div>
            <div class="flex items-center mb-4">
              <div class="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mr-4">
                <MapIcon class="w-6 h-6 text-white" />
              </div>
              <h2 class="text-2xl font-bold text-gray-900">Documentation de fouilles archéologiques</h2>
            </div>
            <p class="text-gray-700 mb-6 leading-relaxed">
              Sur le site archéologique de Pompéi, l'équipe de recherche utilise Anamnesis pour documenter en temps réel chaque découverte. Grâce aux QR codes générés automatiquement, chaque artefact est immédiatement référencé avec ses coordonnées GPS, photos et métadonnées FAIR.
            </p>
            <div class="space-y-3 mb-6">
              <div class="flex items-center">
                <CheckCircleIcon class="w-5 h-5 text-green-500 mr-3" />
                <span class="text-sm text-gray-700">Géolocalisation automatique des découvertes</span>
              </div>
              <div class="flex items-center">
                <CheckCircleIcon class="w-5 h-5 text-green-500 mr-3" />
                <span class="text-sm text-gray-700">QR codes pour traçabilité immédiate</span>
              </div>
              <div class="flex items-center">
                <CheckCircleIcon class="w-5 h-5 text-green-500 mr-3" />
                <span class="text-sm text-gray-700">Synchronisation en temps réel avec l'équipe</span>
              </div>
            </div>
            <router-link 
              to="/cas-usage/fouilles-archeologiques" 
              class="inline-flex items-center bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
            >
              Lire l'étude complète
              <ArrowRightIcon class="w-5 h-5 ml-2" />
            </router-link>
          </div>

          <!-- Cas d'usage 2: Conservation d'œuvres -->
          <div class="bg-gradient-to-br from-teal-50 to-cyan-100 rounded-2xl p-8 shadow-lg">
            <div class="mb-6">
              <img 
                src="https://placeholderimage.eu/api/600/300?category=art" 
                alt="Conservation d'œuvres d'art" 
                class="w-full h-48 object-cover rounded-lg shadow-md"
              />
            </div>
            <div class="flex items-center mb-4">
              <div class="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mr-4">
                <PaletteIcon class="w-6 h-6 text-white" />
              </div>
              <h2 class="text-2xl font-bold text-gray-900">Conservation d'œuvres d'art</h2>
            </div>
            <p class="text-gray-700 mb-6 leading-relaxed">
              Le musée du Louvre utilise Anamnesis pour documenter les interventions de restauration. Chaque étape est photographiée, les produits utilisés sont référencés, et l'historique complet est accessible aux futurs conservateurs.
            </p>
            <div class="space-y-3 mb-6">
              <div class="flex items-center">
                <CheckCircleIcon class="w-5 h-5 text-green-500 mr-3" />
                <span class="text-sm text-gray-700">Suivi détaillé des interventions</span>
              </div>
              <div class="flex items-center">
                <CheckCircleIcon class="w-5 h-5 text-green-500 mr-3" />
                <span class="text-sm text-gray-700">Documentation photographique systématique</span>
              </div>
              <div class="flex items-center">
                <CheckCircleIcon class="w-5 h-5 text-green-500 mr-3" />
                <span class="text-sm text-gray-700">Partage sécurisé entre institutions</span>
              </div>
            </div>
            <router-link 
              to="/cas-usage/conservation-oeuvres" 
              class="inline-flex items-center bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
            >
              Lire l'étude complète
              <ArrowRightIcon class="w-5 h-5 ml-2" />
            </router-link>
          </div>
        </div>

        <!-- Autres cas d'usage -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="bg-white rounded-xl p-6 shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300">
            <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <BuildingIcon class="w-6 h-6 text-blue-600" />
            </div>
            <h3 class="text-lg font-bold text-gray-900 mb-3">Monuments historiques</h3>
            <p class="text-sm text-gray-600 mb-4">
              Documentation des interventions sur Notre-Dame de Paris avec suivi des matériaux et techniques utilisées.
            </p>
            <a href="#" class="text-blue-600 hover:text-blue-700 text-sm font-medium">
              En savoir plus →
            </a>
          </div>

          <div class="bg-white rounded-xl p-6 shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300">
            <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <BookOpenIcon class="w-6 h-6 text-green-600" />
            </div>
            <h3 class="text-lg font-bold text-gray-900 mb-3">Manuscrits anciens</h3>
            <p class="text-sm text-gray-600 mb-4">
              Numérisation et documentation des manuscrits médiévaux de la Bibliothèque nationale de France.
            </p>
            <a href="#" class="text-blue-600 hover:text-blue-700 text-sm font-medium">
              En savoir plus →
            </a>
          </div>

          <div class="bg-white rounded-xl p-6 shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300">
            <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <CameraIcon class="w-6 h-6 text-purple-600" />
            </div>
            <h3 class="text-lg font-bold text-gray-900 mb-3">Photogrammétrie 3D</h3>
            <p class="text-sm text-gray-600 mb-4">
              Modélisation 3D de sites patrimoniaux avec intégration des métadonnées dans Anamnesis.
            </p>
            <a href="#" class="text-blue-600 hover:text-blue-700 text-sm font-medium">
              En savoir plus →
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Témoignages -->
    <section class="py-20 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl font-bold text-gray-900 mb-4">
            Ce que disent nos utilisateurs
          </h2>
          <p class="text-lg text-gray-600">
            Découvrez les retours d'expérience de professionnels du patrimoine
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div class="bg-white rounded-xl p-6 shadow-md">
            <div class="flex items-center mb-4">
              <img 
                src="https://placeholderimage.eu/api/60/60?category=people" 
                alt="Dr. Marie Dubois" 
                class="w-12 h-12 rounded-full mr-4"
              />
              <div>
                <h4 class="font-semibold text-gray-900">Dr. Marie Dubois</h4>
                <p class="text-sm text-gray-600">Archéologue, INRAP</p>
              </div>
            </div>
            <p class="text-gray-700 italic">
              "Anamnesis a révolutionné notre façon de documenter les fouilles. La traçabilité est parfaite et le gain de temps considérable."
            </p>
          </div>

          <div class="bg-white rounded-xl p-6 shadow-md">
            <div class="flex items-center mb-4">
              <img 
                src="https://placeholderimage.eu/api/60/60?category=people" 
                alt="Jean-Pierre Martin" 
                class="w-12 h-12 rounded-full mr-4"
              />
              <div>
                <h4 class="font-semibold text-gray-900">Jean-Pierre Martin</h4>
                <p class="text-sm text-gray-600">Conservateur, Musée d'Orsay</p>
              </div>
            </div>
            <p class="text-gray-700 italic">
              "La collaboration entre équipes n'a jamais été aussi fluide. Anamnesis centralise toute notre documentation de restauration."
            </p>
          </div>

          <div class="bg-white rounded-xl p-6 shadow-md">
            <div class="flex items-center mb-4">
              <img 
                src="https://placeholderimage.eu/api/60/60?category=people" 
                alt="Prof. Elena Rodriguez" 
                class="w-12 h-12 rounded-full mr-4"
              />
              <div>
                <h4 class="font-semibold text-gray-900">Prof. Elena Rodriguez</h4>
                <p class="text-sm text-gray-600">Université de Barcelone</p>
              </div>
            </div>
            <p class="text-gray-700 italic">
              "L'interopérabilité FAIR d'Anamnesis facilite grandement nos échanges de données avec les institutions partenaires."
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-20 bg-gradient-to-r from-purple-600 to-indigo-600">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-3xl font-bold text-white mb-6">
          Votre projet pourrait être le prochain cas d'usage
        </h2>
        <p class="text-xl text-purple-100 mb-8">
          Rejoignez la communauté des professionnels qui transforment la documentation patrimoniale
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <router-link 
            to="/form" 
            class="bg-white text-purple-600 font-semibold py-3 px-8 rounded-lg hover:bg-gray-50 transition-colors duration-300"
          >
            Commencer maintenant
          </router-link>
          <router-link 
            to="/contact" 
            class="bg-purple-700 text-white font-semibold py-3 px-8 rounded-lg border-2 border-purple-500 hover:bg-purple-800 transition-colors duration-300"
          >
            Nous contacter
          </router-link>
        </div>
      </div>
    </section>
  </div>
  <AppFooter />
</template>

<script lang="ts">
import AppFooter from '@/components/UI/AppFooter.vue';
import {
    ArrowRightIcon,
    BookOpenIcon,
    BuildingIcon,
    CameraIcon,
    CheckCircleIcon,
    MapIcon,
    PaletteIcon
} from "lucide-vue-next";
import { defineComponent } from "vue";

export default defineComponent({
  name: "CasUsage",
  components: {
    ArrowRightIcon,
    CheckCircleIcon,
    MapIcon,
    PaletteIcon,
    BuildingIcon,
    BookOpenIcon,
    CameraIcon,
    AppFooter
  }
});
</script>

<style scoped>
.hover\:shadow-lg:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.hover\:bg-gray-50:hover {
  background-color: #f9fafb;
}

.hover\:bg-purple-800:hover {
  background-color: #6b21a8;
}
</style> 